'use client';

import { useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import Link from 'next/link';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowLeft, ArrowUpRight, X } from '@phosphor-icons/react';
import { CheckIcon, CopyIcon, PlusIcon } from '@/components/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { PublicPromotion } from '@/lib/promotion-content';
import { reviewPlatforms } from '@/lib/review-platforms';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';
import { getOrders } from '@/lib/api/orders';
import { getLoyaltyBalance } from '@/lib/api/loyalty';
import { useMoneyRules, type StatusRollType } from '@/lib/money-rules';
import { useStatusRolls } from '@/hooks/use-status-rolls';
import { useAuthStore, selectIsAuthenticatedSafe } from '@/stores/auth-store';
import {
  useCartStore,
  selectHasHydrated,
  selectPromoEligiblePrice,
  selectFirstOrderEligiblePrice,
} from '@/stores/cart-store';
import { ProductPhoto, ProductDetails, usePhotoEdges } from './offer-product';
import { getOfferPhoto } from './offer-photo-source';
import './offer-experience.css';

/**
 * Промокод, по которому ролл за отзыв добавляется в заказ (promo_codes, товар 80).
 * Показывается открыто — решение владельца, 10.2026: поддержка в акции не участвует.
 * То же слово стоит в условиях акции на сервере и в приложении.
 */
const REVIEW_CODE = 'ОТЗЫВ';

const price = (value: number) => `${new Intl.NumberFormat('ru-RU').format(value)}\u00a0₽`;
const number = (value: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);
const tierName = (tier: string) => tier.charAt(0).toUpperCase() + tier.slice(1);

interface Props {
  offer: PublicPromotion | null;
  onClose: () => void;
  triggerRef?: RefObject<HTMLButtonElement | null>;
  initialStatusTier?: StatusRollType;
  initialLoyaltyLevel?: number;
}

export function OfferDialog({
  offer,
  onClose,
  triggerRef,
  initialStatusTier,
  initialLoyaltyLevel,
}: Props) {
  const navigating = useRef(false);
  const content = useRef<HTMLDivElement | null>(null);
  const onNavigate = () => {
    navigating.current = true;
    onClose();
  };
  return (
    <Dialog.Root
      open={!!offer}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="offer-dialog-overlay" />
        <Dialog.Content
          ref={content}
          className="offer-dialog offer-experience"
          data-offer-modal
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            content.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (navigating.current) {
              navigating.current = false;
              return;
            }
            triggerRef?.current?.focus({ preventScroll: true });
          }}
        >
          {offer &&
            (offer.detail_key === 'statusRolls' ? (
              <StatusOffer
                key={offer.id}
                offer={offer}
                onNavigate={onNavigate}
                initialTier={initialStatusTier}
              />
            ) : offer.detail_key === 'loyalty' ? (
              <LoyaltyOffer
                key={offer.id}
                offer={offer}
                onNavigate={onNavigate}
                initialLevel={initialLoyaltyLevel}
              />
            ) : (
              <GiftOffer key={offer.id} offer={offer} onNavigate={onNavigate} />
            ))}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function OfferLayout({
  offer,
  hero,
  children,
  action,
  note,
  scene,
  variant,
  onNavigate,
}: {
  offer: PublicPromotion;
  hero: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  note?: ReactNode;
  scene?: CSSProperties;
  variant?: string;
  onNavigate: () => void;
}) {
  const rules = useRef<HTMLDetailsElement | null>(null);
  const headerThreshold = useRef(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const hasTerms = offer.terms.length > 0 || !!offer.legal_note;
  const showRules = () => {
    if (!rules.current) return;
    rules.current.open = true;
    rules.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    rules.current.querySelector('summary')?.focus({ preventScroll: true });
  };
  return (
    <div
      className={`offer-story offer-story--${variant || offer.detail_key}`}
      style={scene}
      data-scrolled={hasScrolled ? 'true' : undefined}
    >
      <div className="offer-topbar">
        <Dialog.Close className="offer-back" aria-label="Все акции">
          <ArrowLeft size={17} />
          <span>Все акции</span>
        </Dialog.Close>
        <span className="offer-dialog-brand" role="img" aria-label="Азиаточка" />
        <Dialog.Close className="offer-dialog-close" aria-label="Закрыть акцию">
          <X size={24} />
        </Dialog.Close>
      </div>
      <div
        className="offer-dialog-reading"
        data-offer-scroll
        onScroll={(event) => {
          const next = event.currentTarget.scrollTop > 32;
          // Only crossing the header threshold changes React state; scroll itself stays native.
          if (next !== headerThreshold.current) {
            headerThreshold.current = next;
            setHasScrolled(next);
          }
        }}
      >
        <div className="offer-stage">
          {hero}
          <div className="offer-dialog-main">
            <header className="offer-dialog-heading">
              <Dialog.Title>{offer.title}</Dialog.Title>
              <Dialog.Description>{offer.description}</Dialog.Description>
            </header>
            {children}
          </div>
        </div>
        {hasTerms && (
          <div className="offer-rules-wrap">
            <details className="offer-dialog-rules" ref={rules}>
              <summary>
                <span>Подробные условия</span>
                <span className="offer-rules-count">
                  {offer.terms.length || ''}
                  <PlusIcon size={22} />
                </span>
              </summary>
              <div className="offer-rules-content">
                {offer.terms.length > 0 && (
                  <ol>
                    {offer.terms.map((term, i) => (
                      <li key={`${i}-${term}`}>{term}</li>
                    ))}
                  </ol>
                )}
                {offer.legal_note && <p className="offer-dialog-legal">{offer.legal_note}</p>}
              </div>
            </details>
          </div>
        )}
      </div>
      <footer className="offer-dialog-footer">
        {hasTerms ? (
          <button className="offer-footer-info offer-terms-link" onClick={showRules}>
            {note}
            <span className="offer-terms-label">
              Условия акции <ArrowUpRight size={13} />
            </span>
          </button>
        ) : (
          <div className="offer-footer-info">{note}</div>
        )}
        {action || (
          <Link href="/#menu" onClick={onNavigate} className="offer-dialog-primary">
            Выбрать роллы
            <ArrowUpRight size={20} />
          </Link>
        )}
      </footer>
    </div>
  );
}

function ProductHero({
  product,
  loading,
  label,
  tier,
  controls,
  onRetry,
  onSwipe,
}: {
  product?: ProductWithCategories;
  loading: boolean;
  label: string;
  tier?: string;
  controls?: ReactNode;
  onRetry?: () => void;
  onSwipe?: (direction: number) => void;
}) {
  const photo = getOfferPhoto(product);
  const gesture = useRef<{ x: number; y: number } | null>(null);
  const weight = product?.weight ? String(product.weight) : '';
  const serving =
    product?.serving_size &&
    !/^\d+(?:[.,]\d+)?$/.test(product.serving_size) &&
    !weight.includes(product.serving_size)
      ? product.serving_size
      : '';
  return (
    <aside className={`offer-dialog-visual${tier ? ` offer-tier-${tier}` : ''}`}>
      <p className="offer-scene-label">{label}</p>
      {tier && (
        <div className="offer-scene-word" aria-hidden="true" key={tier}>
          {tierName(tier)}
        </div>
      )}
      <div
        className="offer-dialog-product-photo"
        onTouchStart={(event) => {
          const p = event.touches[0];
          gesture.current = { x: p.clientX, y: p.clientY };
        }}
        onTouchEnd={(event) => {
          const start = gesture.current;
          gesture.current = null;
          const p = event.changedTouches[0];
          if (
            start &&
            Math.abs(p.clientX - start.x) > 55 &&
            Math.abs(p.clientX - start.x) > Math.abs(p.clientY - start.y) * 1.5
          )
            onSwipe?.(p.clientX < start.x ? 1 : -1);
        }}
      >
        {product && photo ? (
          <ProductPhoto key={photo} src={photo} name={product.name} />
        ) : (
          <div className="offer-dialog-photo-unavailable" role="status">
            <span>{loading ? 'Загружаем подарок…' : 'Фото подарка временно недоступно'}</span>
            {!loading && onRetry && <button onClick={onRetry}>Повторить</button>}
          </div>
        )}
      </div>
      <div className="offer-dialog-visual-caption" aria-live="polite">
        <h3>{product?.name || 'Ролл в подарок'}</h3>
        <p>
          {weight}
          {weight && !/[а-яa-z]/i.test(weight) ? ' г' : ''}
          {serving ? ` / ${serving}` : ''}
        </p>
      </div>
      {controls}
    </aside>
  );
}

function CartProgress({ threshold, first = false }: { threshold: number; first?: boolean }) {
  const hydrated = useCartStore(selectHasHydrated);
  const statusSum = useCartStore(selectPromoEligiblePrice);
  const firstSum = useCartStore(selectFirstOrderEligiblePrice);
  const sum = first ? firstSum : statusSum;
  const remaining = Math.max(0, threshold - sum);
  if (!hydrated || sum <= 0) return null;
  return (
    <div className="offer-cart-progress" aria-live="polite">
      <div>
        <span>В корзине для акции</span>
        <strong>{price(sum)}</strong>
      </div>
      <progress
        value={Math.min(sum, threshold)}
        max={threshold}
        aria-label="Сумма корзины для акции"
      />
      <p>
        {remaining > 0
          ? `До этого порога ещё ${price(remaining)}`
          : 'Сумма для этого порога набрана'}
      </p>
    </div>
  );
}

function GiftOffer({ offer, onNavigate }: { offer: PublicPromotion; onNavigate: () => void }) {
  const rules = useMoneyRules();
  const authenticated = useAuthStore(selectIsAuthenticatedSafe);
  const user = useAuthStore((state) => state.user);
  const openAuth = useAuthStore((state) => state.openAuthModal);
  const isFirst = offer.detail_key === 'firstOrder';
  const isReview = offer.detail_key === 'review';
  const id = isFirst ? rules.first_order.product_id : isReview ? 80 : 81;
  const product = useQuery({
    queryKey: ['promotion-gift', id],
    queryFn: () => getProductById(id, true),
    staleTime: 60_000,
    retry: false,
  });
  const orders = useQuery({
    queryKey: ['checkout-first-order', user?.id, user?.phone],
    queryFn: () => getOrders(false),
    enabled: isFirst && authenticated,
    staleTime: 0,
    retry: false,
  });
  // Ссылки на площадки отзывов владелец правит в админке; до ответа и при сбое
  // кнопки ведут на запасные адреса (см. reviewPlatforms).
  const settings = useQuery({
    queryKey: ['content-settings'],
    queryFn: async () => (await api.get<unknown>('/content/settings')).data,
    enabled: isReview,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const scene = usePhotoEdges(getOfferPhoto(product.data));
  const login = () => {
    onNavigate();
    requestAnimationFrame(() => openAuth());
  };
  return (
    <OfferLayout
      offer={offer}
      onNavigate={onNavigate}
      scene={scene}
      hero={
        <ProductHero
          product={product.data}
          loading={product.isLoading}
          label={
            isReview
              ? 'Ваш отзыв. Наше спасибо.'
              : isFirst
                ? 'Первое знакомство — с подарком'
                : 'Празднуем вместе'
          }
          onRetry={() => void product.refetch()}
        />
      }
      note={
        isFirst ? (
          <strong>От {price(rules.first_order.min_sum)}</strong>
        ) : (
          <strong>Ролл в подарок</strong>
        )
      }
    >
      {isFirst && (
        <>
          <div className="offer-value">
            <span>К первому заказу от</span>
            <strong>{price(rules.first_order.min_sum)}</strong>
          </div>
          <ol className="offer-howto">
            <li>
              <span>{authenticated ? <CheckIcon size={16} /> : '01'}</span>
              <div>
                {authenticated ? (
                  'Вы вошли в аккаунт'
                ) : (
                  <button onClick={login}>
                    Войти в аккаунт <ArrowUpRight size={15} />
                  </button>
                )}
                <small>
                  {authenticated ? 'Аккаунт подтверждён' : 'Чтобы мы узнали, что вы у нас впервые'}
                </small>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                Собрать заказ<small>Из блюд, участвующих в акции</small>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                Проверить подарок в корзине
                <small>Он добавляется автоматически к первому заказу</small>
              </div>
            </li>
          </ol>
          {authenticated && (
            <p className="offer-account-check" role="status">
              {orders.isPending
                ? 'Проверяем доступность подарка…'
                : orders.isError
                  ? 'Не удалось проверить историю заказов.'
                  : orders.data?.length === 0
                    ? 'В вашей истории пока нет заказов.'
                    : 'В истории уже есть заказы. Этот подарок действует только на первый.'}
              {orders.isError && <button onClick={() => void orders.refetch()}>Повторить</button>}
            </p>
          )}
          <CartProgress threshold={rules.first_order.min_sum} first />
        </>
      )}
      {isReview && (
        <div className="offer-review-flow">
          <ol className="offer-howto">
            <li>
              <span>01</span>
              <div>
                Оставьте отзыв<small>На Яндекс Картах, в 2ГИС или ВКонтакте</small>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                Введите промокод {REVIEW_CODE} в корзине
                <small>Поле «У меня есть промокод» — при оформлении заказа</small>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                Получите ролл в подарок<small>Он добавится к заказу</small>
              </div>
            </li>
          </ol>
          <div className="offer-review-places">
            <p>
              Где оставить отзыв<small>Ролл доступен 3 раза, за 1 отзыв на каждой площадке</small>
            </p>
            <ul>
              {reviewPlatforms(settings.data).map((platform) => (
                <li key={platform.key}>
                  <a
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Оставить отзыв: ${platform.name}`}
                  >
                    <span
                      className="offer-place-mark"
                      data-long={platform.mark.length > 2 ? '' : undefined}
                      aria-hidden="true"
                    >
                      {platform.mark}
                    </span>
                    <ArrowUpRight size={15} aria-hidden="true" />
                    {platform.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <PromoCode code={REVIEW_CODE} caption="Промокод за отзыв" />
        </div>
      )}
      {offer.detail_key === 'birthday' && <PromoCode code="ДР" caption="Ваш праздничный промокод" />}
      <ProductDetails product={product.data} />
    </OfferLayout>
  );
}

function PromoCode({ code, caption }: { code: string; caption: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setState('copied');
    } catch {
      setState('error');
    }
  };
  return (
    <div className="offer-birthday-pass">
      <button
        className="offer-dialog-code"
        data-long={code.length > 3 ? '' : undefined}
        onClick={copy}
        aria-label={`Скопировать промокод ${code}`}
      >
        <span>
          <small>{caption}</small>
          <strong>{code}</strong>
        </span>
        <span className="offer-code-copy">
          {state === 'copied' ? <CheckIcon size={24} /> : <CopyIcon size={24} />}
          <small>{state === 'copied' ? 'Скопирован' : 'Скопировать'}</small>
        </span>
      </button>
      <p role="status">
        {state === 'error'
          ? `Не получилось скопировать. Введите ${code} в корзине вручную.`
          : state === 'copied'
            ? 'Промокод скопирован. Примените его в корзине.'
            : 'Нажмите на купон, чтобы скопировать. Примените при оформлении заказа.'}
      </p>
    </div>
  );
}

function StatusOffer({
  offer,
  onNavigate,
  initialTier,
}: {
  offer: PublicPromotion;
  onNavigate: () => void;
  initialTier?: StatusRollType;
}) {
  const rules = useMoneyRules();
  const status = useStatusRolls();
  const [tier, setTier] = useState<StatusRollType>(initialTier ?? 'silver');
  const options = [...rules.status_rolls].sort((a, b) => a.min_sum - b.min_sum);
  const active = options.find((option) => option.type === tier) ?? options[0];
  const product = active ? status.rolls[active.type] : undefined;
  const scene = usePhotoEdges(getOfferPhoto(product));
  const switchGift = (direction: number) => {
    const index = options.findIndex((option) => option.type === active?.type);
    const next = options[(index + direction + options.length) % options.length];
    if (next) setTier(next.type);
  };
  const tabs = (
    <fieldset className="offer-dialog-tiers">
      <legend>Посмотреть подарки</legend>
      <div>
        {options.map((option) => (
          <label key={option.type}>
            <input
              type="radio"
              name="offer-status-tier"
              value={option.type}
              checked={active?.type === option.type}
              onChange={() => setTier(option.type)}
            />
            <span>
              <strong>{tierName(option.type)}</strong>
              <small>от {price(option.min_sum)}</small>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
  return (
    <OfferLayout
      offer={offer}
      onNavigate={onNavigate}
      scene={scene}
      variant="status"
      hero={
        <ProductHero
          product={product}
          loading={status.isLoading}
          label="Три статуса. Три подарка."
          tier={active?.type}
          controls={tabs}
          onSwipe={switchGift}
        />
      }
      note={<strong>{active ? `От ${price(active.min_sum)}` : 'Ролл в подарок'}</strong>}
    >
      <div className="offer-value" key={active?.type}>
        <span>{active ? `${tierName(active.type)} — к заказу от` : 'К заказу от'}</span>
        <strong>{price(active?.min_sum || 0)}</strong>
      </div>
      <p className="offer-status-explainer">
        Сумма растёт — подарок становится интереснее. В заказ добавляется один ролл за самый высокий
        достигнутый порог.
      </p>
      <div className="offer-threshold-track" aria-label="Пороги статусных подарков">
        {options.map((option) => (
          <button
            key={option.type}
            onClick={() => setTier(option.type)}
            aria-pressed={active?.type === option.type}
          >
            <span className="offer-threshold-dot" />
            <strong>{price(option.min_sum)}</strong>
            <small>{tierName(option.type)}</small>
          </button>
        ))}
      </div>
      {active && <CartProgress threshold={active.min_sum} />}
      <ProductDetails product={product} />
    </OfferLayout>
  );
}

function LoyaltyOffer({
  offer,
  onNavigate,
  initialLevel,
}: {
  offer: PublicPromotion;
  onNavigate: () => void;
  initialLevel?: number;
}) {
  const authenticated = useAuthStore(selectIsAuthenticatedSafe);
  const user = useAuthStore((state) => state.user);
  const openAuth = useAuthStore((state) => state.openAuthModal);
  const rules = useMoneyRules();
  const query = useQuery({
    queryKey: ['promotion-loyalty-balance', user?.id, user?.phone],
    queryFn: getLoyaltyBalance,
    enabled: authenticated,
    staleTime: 30_000,
    retry: false,
  });
  const data = authenticated ? query.data : undefined;
  const levels = [...(data?.all_levels?.length ? data.all_levels : rules.loyalty.levels)].sort(
    (a, b) => a.min_total - b.min_total || a.level - b.level
  );
  const [previewLevel, setPreviewLevel] = useState<number | null>(initialLevel ?? null);
  const selected =
    levels.find((level) => level.level === (previewLevel ?? data?.level.current)) ??
    levels[levels.length - 1];
  const includesDelivery = (data?.spend_base ?? rules.loyalty.spend_base) === 'items_plus_delivery';
  const maxSpend = data?.max_spend_percent ?? rules.loyalty.max_spend_percent;
  const minimum = data?.min_cart_for_spend ?? rules.loyalty.min_cart_for_spend;
  return (
    <OfferLayout
      offer={offer}
      onNavigate={onNavigate}
      variant="loyalty"
      hero={
        <aside className="offer-dialog-visual offer-loyalty-scene">
          <p className="offer-scene-label">Ваш уровень удовольствия</p>
          <div className="offer-member-card" data-level={selected?.level}>
            <div>
              <span className="offer-dialog-brand" role="img" aria-label="Азиаточка" />
              <span>{selected?.name}</span>
            </div>
            <strong key={selected?.level}>
              {number(selected?.earn_rate || 0)}
              <span>%</span>
            </strong>
            <p>Возвращаем коинами</p>
            <small>
              {selected?.min_total
                ? `От ${price(selected.min_total)} за всё время`
                : 'С первого заказа'}
            </small>
            <img src="/storefront/offers-v2-loyalty.webp" alt="" width={1536} height={1024} />
          </div>
          <p className="offer-preview-caption">Посмотрите, что даёт каждый уровень</p>
          <div className="offer-level-picker" role="group" aria-label="Уровни лояльности">
            {levels.map((level) => (
              <button
                key={level.level}
                aria-pressed={selected?.level === level.level}
                onClick={() => setPreviewLevel(level.level)}
              >
                <span>{level.name}</span>
                <strong>{number(level.earn_rate)}%</strong>
              </button>
            ))}
          </div>
        </aside>
      }
      note={<strong>1 коин = 1 ₽</strong>}
      action={
        authenticated ? (
          <Link href="/profile" onClick={onNavigate} className="offer-dialog-primary">
            Мои коины
            <ArrowUpRight size={20} />
          </Link>
        ) : (
          <button
            className="offer-dialog-primary"
            onClick={() => {
              onNavigate();
              requestAnimationFrame(() => openAuth());
            }}
          >
            Войти и копить коины
            <ArrowUpRight size={20} />
          </button>
        )
      }
    >
      {data && (
        <div className="offer-dialog-balance">
          <span>Ваш баланс</span>
          <strong>
            {number(data.balance)} <small>коинов</small>
          </strong>
          <p>
            {data.level.name} · возвращаем {number(data.level.earn_rate)}%
          </p>
          {data.level.next_min !== null ? (
            <div className="offer-loyalty-progress">
              <progress
                value={Math.max(0, Math.min(100, data.level.progress_percent))}
                max={100}
                aria-label="Прогресс до следующего уровня"
              />
              <span>
                До {data.level.next_name} — ещё{' '}
                {price(Math.max(0, data.level.next_min - data.level.lifetime_total))}
              </span>
            </div>
          ) : (
            <p>У вас максимальный уровень программы.</p>
          )}
        </div>
      )}
      {authenticated && query.isPending && <p role="status">Загружаем ваш баланс…</p>}
      {authenticated && query.isError && (
        <p role="status">
          Не удалось загрузить баланс.{' '}
          <button className="offer-inline-link" onClick={() => void query.refetch()}>
            Повторить
          </button>
        </p>
      )}
      <div className="offer-loyalty-benefits">
        <div>
          <strong>
            {number(maxSpend)}
            <span>%</span>
          </strong>
          <p>
            Можно оплатить коинами
            <br />
            от суммы {includesDelivery ? 'заказа с доставкой' : 'блюд'}
          </p>
        </div>
        <div>
          <strong>{price(minimum)}</strong>
          <p>
            Минимум для списания
            <br />
            {includesDelivery ? 'с учётом доставки' : 'без учёта доставки'}
          </p>
        </div>
      </div>
      <p className="offer-status-explainer">
        Уровень зависит от общей суммы доставленных заказов. Если списываете коины, новые за этот
        заказ не начисляются.
      </p>
      <details className="offer-dialog-product-details offer-dialog-levels">
        <summary>
          Все уровни программы <PlusIcon size={20} />
        </summary>
        <div className="offer-dialog-level-list">
          {levels.map((level) => (
            <div
              key={level.level}
              className={data?.level.current === level.level ? 'is-current' : undefined}
            >
              <span>
                {level.name}
                {data?.level.current === level.level && <small>Ваш уровень</small>}
              </span>
              <span>{level.min_total ? `От ${price(level.min_total)}` : 'С первого заказа'}</span>
              <strong>{number(level.earn_rate)}%</strong>
            </div>
          ))}
        </div>
      </details>
    </OfferLayout>
  );
}
